<template>
    <v-dialog 
      v-model="showModal" 
      :max-width="isMobile ? '95vw' : '1000px'" 
      :fullscreen="isMobile"
      scrollable
      transition="dialog-bottom-transition"
    >
      <v-card class="changelog-card">
        <!-- Header com gradiente -->
        <v-card-title class="changelog-header pa-6">
          <div class="d-flex justify-space-between align-center w-100">
            <div class="d-flex align-center">
              <v-icon size="32" color="white" class="mr-3">mdi-history</v-icon>
              <div>
                <h2 class="text-h5 text-white font-weight-bold mb-1">Changelog</h2>
                <p class="text-body-2 text-white opacity-90 mb-0">Histórico de Versões</p>
              </div>
            </div>
            <v-btn 
              icon 
              variant="text" 
              color="white"
              size="large"
              @click="closeDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular 
            indeterminate 
            color="primary" 
            size="64"
          ></v-progress-circular>
          <h3 class="mt-6 text-h6 text-grey-darken-1">Carregando changelog...</h3>
          <p class="text-body-2 text-grey">Aguarde um momento</p>
        </div>
        
        <!-- Error State -->
        <v-card-text v-else-if="error" class="pa-6">
          <v-alert 
            type="error" 
            variant="tonal"
            class="mb-4"
            border="start"
            border-color="error"
          >
            <template v-slot:prepend>
              <v-icon>mdi-alert-circle</v-icon>
            </template>
            <div>
              <h4 class="text-h6 mb-2">Ops! Algo deu errado</h4>
              <p class="mb-3">{{ error }}</p>
              <v-btn 
                @click="loadChangelog" 
                color="error" 
                variant="outlined"
                prepend-icon="mdi-refresh"
                size="small"
              >
                Tentar Novamente
              </v-btn>
            </div>
          </v-alert>
        </v-card-text>
        
        <!-- Content -->
        <v-card-text v-else class="changelog-content">
          <div v-if="changelog && changelog.length > 0">

  
            <!-- Timeline -->
            <div class="timeline-container">
              <div 
                v-for="(version, index) in changelog"
                :key="index"
                class="timeline-item"
                :class="{ 'is-last': index === changelog.length - 1 }"
              >
                <!-- Timeline Icon -->
                <div class="timeline-marker">
                  <v-avatar 
                    :color="getVersionColor(index)" 
                    size="48"
                    class="timeline-avatar elevation-4"
                  >
                    <v-icon color="white" size="24">
                      {{ getVersionIcon(index) }}
                    </v-icon>
                  </v-avatar>
                </div>
  
                <!-- Timeline Content -->
                <div class="timeline-content">
                  <v-card 
                    class="version-card elevation-3"
                    :class="`border-${getVersionColor(index)}`"
                  >
                    <!-- Version Header -->
                    <v-card-title class="version-title pa-4">
                      <div class="d-flex justify-space-between align-center w-100">
                        <div>
                          <h3 class="text-h6 font-weight-bold mb-1">
                            Versão {{ version.version }}
                          </h3>
                          <p class="text-body-2 text-grey-darken-1 mb-0">
                            {{ getSectionCount(version.sections) }} alterações realizadas
                          </p>
                        </div>
                        <v-chip
                          :color="getVersionColor(index)"
                          size="small"
                          variant="flat"
                          class="font-weight-bold"
                        >
                          <v-icon start size="16">mdi-calendar</v-icon>
                          {{ version.date }}
                        </v-chip>
                      </div>
                    </v-card-title>
  
                    <v-divider></v-divider>
  
                    <!-- Sections -->
                    <v-card-text class="pa-4">
                      <div 
                        v-for="(items, sectionName) in version.sections" 
                        :key="sectionName"
                        class="section-group mb-4"
                      >
                        <!-- Section Header -->
                        <div class="section-title mb-3">
                          <div class="d-flex align-center">
                            <v-icon
                              :color="getSectionColor(sectionName)"
                              size="20"
                              class="mr-2"
                            >
                              {{ getSectionIcon(sectionName) }}
                            </v-icon>
                            <h4 
                              class="text-subtitle-1 font-weight-bold mb-0" 
                              :class="`text-${getSectionColor(sectionName)}`"
                            >
                              {{ sectionName }}
                            </h4>
                            <v-chip
                              :color="getSectionColor(sectionName)"
                              size="x-small"
                              variant="tonal"
                              class="ml-2"
                            >
                              {{ items.length }}
                            </v-chip>
                          </div>
                        </div>
  
                        <!-- Section Items -->
                        <div class="section-items">
                          <div 
                            v-for="(item, itemIndex) in items"
                            :key="itemIndex"
                            class="section-item d-flex align-start mb-2"
                          >
                            <v-icon
                              :color="getSectionColor(sectionName)"
                              size="16"
                              class="mr-3 mt-1 flex-shrink-0"
                            >
                              mdi-circle-small
                            </v-icon>
                            <p class="text-body-2 mb-0 flex-grow-1">
                              {{ item }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Empty State -->
          <div v-else class="empty-state">
            <v-icon size="120" color="grey-lighten-2">mdi-file-document-outline</v-icon>
            <h3 class="text-h5 mt-6 mb-3 text-grey-darken-1">Nenhuma versão encontrada</h3>
            <p class="text-body-1 text-grey mb-6">
              O arquivo de changelog ainda não foi criado ou está vazio.
            </p>
            <v-btn 
              @click="loadChangelog" 
              color="primary" 
              variant="outlined"
              prepend-icon="mdi-refresh"
            >
              Tentar Carregar
            </v-btn>
          </div>
        </v-card-text>
        

      </v-card>
    </v-dialog>
  </template>
  
  <script>
  export default {
    name: 'ChangelogModal',
    props: {
      showChangelogModal: Boolean,
    },
    data() {
      return {
        showModal: this.showChangelogModal,
        changelog: [],
        loading: false,
        error: null,
      };
    },
    computed: {
      isMobile() {
        return this.$vuetify.display.mobile;
      },
      latestVersion() {
        return this.changelog.length > 0 ? this.changelog[0] : null;
      }
    },
    watch: {
      showChangelogModal: {
        handler(newValue) {
          this.showModal = newValue;
          if (newValue && this.changelog.length === 0) {
            this.loadChangelog();
          }
        },
        immediate: true,
      },
    },
    methods: {
      async loadChangelog() {
        this.loading = true;
        this.error = null;
        
        try {
          // Em produção, os arquivos da pasta public são copiados para a raiz
          // Adicionamos um parâmetro de timestamp para evitar o cache do navegador
          const timestamp = new Date().getTime();
          const response = await fetch(`./changelog.json?v=${timestamp}`);
          
          if (!response.ok) {
            throw new Error('Arquivo changelog.json não encontrado');
          }
          
          const data = await response.json();
          this.changelog = data.versions || [];
          console.log('Changelog carregado com sucesso:', data);
          
        } catch (error) {
          console.error('Erro ao carregar changelog:', error);
          this.error = 'Erro ao carregar o changelog. Verifique se o arquivo changelog.json existe na pasta public.';
        } finally {
          this.loading = false;
        }
      },
      
      closeDialog() {
        this.$emit("update:showChangelogModal", false);
      },
      
      getSectionColor(sectionName) {
        const colors = {
          'Adicionado': 'success',
          'Melhorado': 'info',
          'Corrigido': 'warning',
          'Removido': 'error',
          'Alterado': 'purple',
          'Atualizado': 'teal',
          'Segurança': 'red-darken-2'
        };
        return colors[sectionName] || 'primary';
      },
      
      getSectionIcon(sectionName) {
        const icons = {
          'Adicionado': 'mdi-plus-circle',
          'Melhorado': 'mdi-arrow-up-circle',
          'Corrigido': 'mdi-wrench',
          'Removido': 'mdi-minus-circle',
          'Alterado': 'mdi-pencil-circle',
          'Atualizado': 'mdi-update',
          'Segurança': 'mdi-shield-check'
        };
        return icons[sectionName] || 'mdi-information';
      },
      
      getVersionColor(index) {
        const colors = ['success', 'info', 'warning', 'secondary', 'purple', 'teal'];
        return colors[index % colors.length];
      },
  
      getVersionIcon(index) {
        const icons = ['mdi-rocket-launch', 'mdi-star', 'mdi-cog', 'mdi-puzzle', 'mdi-lightbulb', 'mdi-heart'];
        return icons[index % icons.length];
      },
  
      getSectionCount(sections) {
        return Object.values(sections).reduce((total, items) => total + items.length, 0);
      }
    },
  };
  </script>
  
  <style scoped>
  .changelog-card {
    border-radius: 16px !important;
    overflow: hidden;
  }
  
  .changelog-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    border-radius: 0 !important;
  }
  
  .changelog-content {
    max-height: 70vh;
    overflow-y: auto;
    padding: 24px !important;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }
  
  .stats-container {
    margin-bottom: 32px;
  }
  
  /* Timeline Styles */
  .timeline-container {
    position: relative;
    padding-left: 24px;
  }
  
  .timeline-item {
    position: relative;
    padding-bottom: 40px;
  }
  
  .timeline-item:not(.is-last)::after {
    content: '';
    position: absolute;
    left: 24px;
    top: 48px;
    bottom: -40px;
    width: 2px;
    background: linear-gradient(to bottom, #e0e0e0, #f5f5f5);
  }
  
  .timeline-marker {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 2;
  }
  
  .timeline-avatar {
    border: 3px solid white !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }
  
  .timeline-content {
    margin-left: 72px;
    margin-top: 8px;
  }
  
  .version-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }
  
  .version-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  }
  
  .version-card.border-success {
    border-left-color: #4caf50 !important;
  }
  
  .version-card.border-info {
    border-left-color: #2196f3 !important;
  }
  
  .version-card.border-warning {
    border-left-color: #ff9800 !important;
  }
  
  .version-card.border-secondary {
    border-left-color: #9e9e9e !important;
  }
  
  .version-card.border-purple {
    border-left-color: #9c27b0 !important;
  }
  
  .version-card.border-teal {
    border-left-color: #009688 !important;
  }
  
  .version-title {
    background: rgba(0, 0, 0, 0.02);
  }
  
  .section-group:last-child {
    margin-bottom: 0 !important;
  }
  
  .section-title {
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  
  .section-items {
    padding-left: 8px;
  }
  
  .section-item {
    transition: background-color 0.2s ease;
    border-radius: 4px;
    padding: 4px 8px;
    margin: 0 -8px 8px -8px;
  }
  
  .section-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  
  /* Responsividade */
  @media (max-width: 600px) {
    .changelog-content {
      max-height: calc(100vh - 200px);
      padding: 16px !important;
    }
    
    .timeline-container {
      padding-left: 16px;
    }
    
    .timeline-marker .timeline-avatar {
      width: 40px !important;
      height: 40px !important;
    }
    
    .timeline-content {
      margin-left: 56px;
    }
    
    .timeline-item:not(.is-last)::after {
      left: 20px;
      top: 40px;
    }
    
    .stats-container {
      margin-bottom: 24px;
    }
  }
  
  /* Scrollbar personalizada */
  .changelog-content::-webkit-scrollbar {
    width: 8px;
  }
  
  .changelog-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .changelog-content::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  .changelog-content::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  </style>